import { ALE_KF_2026, allocateSeats } from '@val/engine'
import type {
  AleResult,
  CountingProgress,
  PartyResult,
  RecentDistrict,
  Timeline,
  TimelinePoint,
} from '@val/shared'
import { ALE_2022, getParty, type Phase } from '@val/shared'

/** Ales valdistrikt, i den ordning de "rapporterar in" i demoläget. */
const DISTRICT_NAMES = [
  'Alafors västra',
  'Älvängen södra',
  'Surte södra',
  'Skepplanda västra',
  'Alafors östra',
  'Surte norra',
  'Bohus norra',
  'Älvängen östra',
  'Älvängen centrum',
  'Bohus södra',
  'Nödinge östra',
  'Nödinge västra',
  'Skepplanda östra',
  'Surte västra',
  'Bohus västra',
  'Nödinge norra',
  'Nödinge södra',
  'Skepplanda norra',
  'Älvängen norra',
  'Alafors norra',
]

/** De senast "rapporterade" distrikten i demoläget, nyast först. */
function recentMockDistricts(counted: number): RecentDistrict[] {
  const revealed = DISTRICT_NAMES.slice(0, counted)
  const now = Date.now()
  return revealed
    .map((name, i) => ({
      name,
      reportedAt: new Date(now - (counted - 1 - i) * 5 * 60_000).toISOString(),
      turnoutPct: 75 + ((name.length * 7) % 15),
      validVotes: 600 + ((name.length * 53) % 500),
    }))
    .reverse()
    .slice(0, 5)
}

/**
 * Demodata som härmar en valnatt: uppräkningen går från 0 → 100 % under ca 5 minuter
 * från första sidladdning, och landar nära 2022 års resultat med lite drift.
 * Används bara när riktig `/api` inte svarar (lokal utveckling / torrkörning).
 */

const FULL_COUNT_MS = 4 * 60_000
// Börja en bit in i uppräkningen så demovyn direkt visar siffror.
const START = Date.now() - 0.2 * FULL_COUNT_MS
const TOTAL_AREAS = 20

// Liten, fast "drift" mot 2022 så resultatet inte är identiskt.
const DRIFT: Record<string, number> = { C: 1.18, S: 0.94, M: 1.06, SD: 1.05, MP: 0.8, KD: 0.9 }

function projectedVotes(): Record<string, number> {
  const out: Record<string, number> = {}
  for (const p of ALE_2022.parties) {
    out[p.code] = Math.round(p.votes * (DRIFT[p.code] ?? 1))
  }
  return out
}

function progress(): number {
  return Math.min(1, (Date.now() - START) / FULL_COUNT_MS)
}

/** Distrikt räknas i klump; röstetal skalas linjärt med lite brus per omgång. */
function countedAtProgress(t: number): number {
  return Math.round(t * TOTAL_AREAS)
}

export function mockResult(phase: Phase): AleResult {
  const t = progress()
  const counted = countedAtProgress(t)
  const countedRatio = counted / TOTAL_AREAS
  const full = projectedVotes()

  // Tidiga distrikt skevar lite; jämnar ut mot slutet.
  const skew = 1 - countedRatio
  const votesByCode: Record<string, number> = {}
  for (const [code, v] of Object.entries(full)) {
    const wobble = 1 + skew * 0.12 * Math.sin((code.charCodeAt(0) + counted) * 1.3)
    votesByCode[code] = Math.max(0, Math.round(v * countedRatio * wobble))
  }

  const validVotes = Object.values(votesByCode).reduce((a, b) => a + b, 0)
  const totalValidFull = Object.values(full).reduce((a, b) => a + b, 0)

  const computed =
    validVotes > 0
      ? allocateSeats(
          Object.entries(votesByCode).map(([party, votes]) => ({ party, votes })),
          { ...ALE_KF_2026, totalValidVotes: validVotes },
        ).seats
      : {}

  const parties: PartyResult[] = ALE_2022.parties
    .map((p): PartyResult => {
      const votes = votesByCode[p.code] ?? 0
      return {
        code: p.code,
        name: getParty(p.code).name,
        votes,
        pct: validVotes > 0 ? (votes / validVotes) * 100 : 0,
        seatsOfficial: computed[p.code] ?? 0,
        seatsComputed: computed[p.code] ?? 0,
        votes2022: p.votes,
        seats2022: p.seats,
      }
    })
    .sort((a, b) => b.votes - a.votes)

  const now = new Date().toISOString()
  return {
    phase,
    updatedAt: now,
    fetchedAt: now,
    sourceMd5: `mock-${counted}`,
    municipality: 'Ale',
    municipalityCode: '1440',
    totalSeats: ALE_KF_2026.seats,
    majority: Math.floor(ALE_KF_2026.seats / 2) + 1,
    countedAreas: counted,
    totalAreas: TOTAL_AREAS,
    countedRatio,
    turnoutPct: countedRatio > 0 ? 80 + 3 * countedRatio : null,
    validVotes,
    invalidVotes: Math.round(validVotes * 0.016),
    parties,
    mismatch: false,
    final: phase === 'slutlig' && countedRatio >= 1,
    recentDistricts: recentMockDistricts(counted),
    // hjälpvärde för ev. felsökning
    ...(import.meta.env.DEV ? { _projectedTotal: totalValidFull } : {}),
  } as AleResult
}

const NATIONAL_TOTAL_AREAS = 6626

/** Demo för räkningsstatus – används bara om `/api/progress` inte svarar. */
export function mockProgress(_phase: Phase): CountingProgress {
  const t = progress()
  const counted = countedAtProgress(t)
  const now = new Date().toISOString()
  const full = projectedVotes()
  const valid = Object.values(full).reduce((a, b) => a + b, 0)
  // Riksdagens röstfördelning är en annan siffra än kommunvalets, men i
  // avsaknad av riktig riksdagsdata i mock-läget återanvänder vi samma bas.
  const riksdagVotes =
    counted > 0
      ? ALE_2022.parties
          .map((p) => ({
            code: p.code,
            name: getParty(p.code).name,
            votes: full[p.code] ?? 0,
            pct: valid > 0 ? ((full[p.code] ?? 0) / valid) * 100 : 0,
          }))
          .sort((a, b) => b.votes - a.votes)
      : undefined

  return {
    updatedAt: now,
    fetchedAt: now,
    municipality: 'Ale',
    municipalityCode: '1440',
    countedAreas: counted,
    totalAreas: TOTAL_AREAS,
    countedRatio: counted / TOTAL_AREAS,
    nationalCountedAreas: Math.round(t * NATIONAL_TOTAL_AREAS),
    nationalTotalAreas: NATIONAL_TOTAL_AREAS,
    nationalCountedRatio: t,
    ...(riksdagVotes ? { riksdagVotes } : {}),
  }
}

export function mockTimeline(phase: Phase): Timeline {
  const points: TimelinePoint[] = []
  const t = progress()
  const steps = Math.max(1, Math.round(t * 12))
  for (let i = 1; i <= steps; i++) {
    const frac = i / 12
    const counted = Math.round(frac * TOTAL_AREAS)
    const full = projectedVotes()
    const votes: Record<string, number> = {}
    for (const [code, v] of Object.entries(full)) votes[code] = Math.round(v * frac)
    const valid = Object.values(votes).reduce((a, b) => a + b, 0)
    const seats =
      valid > 0
        ? allocateSeats(
            Object.entries(votes).map(([party, n]) => ({ party, votes: n })),
            { ...ALE_KF_2026, totalValidVotes: valid },
          ).seats
        : {}
    points.push({
      fetchedAt: new Date(START + frac * FULL_COUNT_MS).toISOString(),
      updatedAt: new Date(START + frac * FULL_COUNT_MS).toISOString(),
      countedAreas: counted,
      totalAreas: TOTAL_AREAS,
      countedRatio: counted / TOTAL_AREAS,
      turnoutPct: 80 + 3 * (counted / TOTAL_AREAS),
      seats,
      votes,
    })
  }
  return { phase, points }
}
